import { useEffect } from 'react';
import { useTypedSelector } from 'redux/reducers';
import { attachCurrentCosneticsSession, getSessionCoordinator, reportSessionError } from 'services/session';
import { SESSION_STORAGE_PREFIX } from 'services/session/SessionCoordinator';

const CHECK_INTERVAL_MS = 30 * 1000;
const ACTIVITY_WRITE_INTERVAL_MS = 1000;
// Browser-generated scroll events can also come from code (e.g. scrollIntoView).
// Wheel/key/touch input records the person's action without extending idle time
// for automatic page movement.
const activityEvents = ['pointermove', 'pointerdown', 'keydown', 'touchstart', 'wheel'];

/** Event wiring only; the service owns session clocks and cross-tab coordination. */
export const useExpiration = () => {
  const userId = useTypedSelector((state) => state.user.user.Id);
  const baseURL = useTypedSelector((state) => state.config.baseURL || state.config.hostname?.baseURL);
  const enabled = useTypedSelector((state) => Boolean(state.user.cosnetics && state.user.auth.token));

  useEffect(() => {
    if (enabled) void attachCurrentCosneticsSession().catch(reportSessionError);
  }, [enabled, userId, baseURL]);

  useEffect(() => {
    if (!userId || !baseURL) return;
    const identity = { userId, baseURL };
    const coordinator = getSessionCoordinator();
    let lastWrite = 0;
    let checking = false;
    let stopped = false;
    const check = async () => {
      if (stopped || checking) return;
      checking = true;
      try { await coordinator.check(identity); }
      catch (error) { reportSessionError(error); }
      finally { checking = false; }
    };
    const onActivity = (event: Event) => {
      // Programmatic events and background work are not human activity.
      if (!event.isTrusted || stopped || Date.now() - lastWrite < ACTIVITY_WRITE_INTERVAL_MS) return;
      lastWrite = Date.now();
      void coordinator.activity(identity).then(check).catch(reportSessionError);
    };
    const onResume = () => { void check(); };
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key.startsWith(SESSION_STORAGE_PREFIX)) {
        void coordinator.sync(identity).catch(reportSessionError);
      }
    };
    activityEvents.forEach((event) => window.addEventListener(event, onActivity, { passive: true, capture: true }));
    window.addEventListener('focus', onResume);
    window.addEventListener('online', onResume);
    window.addEventListener('pageshow', onResume);
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onResume);
    document.addEventListener('resume', onResume);
    const interval = window.setInterval(onResume, CHECK_INTERVAL_MS);
    void check();
    return () => {
      stopped = true;
      window.clearInterval(interval);
      activityEvents.forEach((event) => window.removeEventListener(event, onActivity, true));
      window.removeEventListener('focus', onResume);
      window.removeEventListener('online', onResume);
      window.removeEventListener('pageshow', onResume);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onResume);
      document.removeEventListener('resume', onResume);
    };
  }, [userId, baseURL]);
};

export default useExpiration;
